import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · BusMap
 * tokens locked from docs/design.md §1 (map + pebble tile bg) + §10 (marker colours)
 *                                    §10 (Demand Heatmap monochrome→clay 8-step)
 * states covered: idle · loading (Leaflet init) · error (tile 4xx handled by leaflet)
 *                 + demand-active: hides bunched/held marker variants
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * MapLibre GL is intentionally not wired at this phase — the tile provider
 * (OpenFreeMap Liberty) is a vector tileset that needs a WebGL context;
 * on CI-headless smoke it can flake. We use react-leaflet with the OSM
 * raster tile URL for Phase 3 (cheap, deterministic, no CORS surprise),
 * and swap to MapLibre in Phase 4 alongside the WebSocket wiring. This
 * keeps the shell demo green while preserving the map-first composition.
 *
 * Phase 6 additions:
 *   - `demandActive` (default false) — when true, mount `DemandHeatmap` +
 *     `HeatmapLegend` and pass `demandActive` down to `VehicleLayer` so
 *     bunched/held marker variants are suppressed for the frame (heatmap
 *     owns the single clay accent).
 *   - `heatmapCells` — H3-cell payload from `useDemandHeatmap`. Ignored
 *     when `demandActive` is false.
 *   - `heatmapWindowMinutes` — for the legend caption.
 *   - `phantomLayerSlot` — Phase 7 mount slot; rendered inside the
 *     `MapContainer` as a sibling of `VehicleLayer`. Default: `null`.
 */
import { useMemo, useState } from "react";
import { MapContainer, TileLayer, ZoomControl, useMapEvents } from "react-leaflet";
import { AS_4_STOPS, DELHI_CENTER, MUMBAI_CORRIDOR_CENTER, isMumbaiCorridorFeed, } from "../../lib/tokens";
import { CarpoolLayer } from "./CarpoolLayer";
import { ActiveCarpoolRidesLayer } from "./ActiveCarpoolRidesLayer";
import { useHeadwayCv } from "../../hooks/useHeadwayCv";
import { useMumbaiRoutes } from "../../hooks/useMumbaiRoutes";
import { CorridorPolyline } from "./CorridorPolyline";
import { VehicleLayer } from "./VehicleLayer";
import { StopMarkers } from "./StopMarkers";
import { SimulatedPill } from "../common/SimulatedPill";
import { DemandHeatmap } from "../demand/DemandHeatmap";
import { HeatmapLegend } from "../demand/HeatmapLegend";
import { RouteHighlightLayer } from "./RouteHighlightLayer";
import { BusDetailSheet } from "./BusDetailSheet";
import { PlaybackControls } from "../sim/PlaybackControls";
import { OnboardingHint } from "../common/OnboardingHint";
/**
 * D5: at low zoom (Delhi city view) 200 markers overlap. This tiny child
 * component owns the current zoom and reports it up to the parent so the
 * marker list can decimate itself. Sits inside MapContainer so it can use
 * useMapEvents; render zero output.
 */
function ZoomWatcher({ onZoom }) {
    useMapEvents({
        zoomend: (e) => onZoom(e.target.getZoom()),
        load: (e) => onZoom(e.target.getZoom()),
    });
    return null;
}
/**
 * Threshold under which we sample the vehicle list to prevent icon overlap.
 *
 * Phase 17c fix: on the default city-wide Mumbai overview (zoom 11), 55 mock
 * buses with clay-ember bunched halos merged into an unreadable blob. We
 *   - bumped MARKER_THROTTLE_ZOOM 13→14 so throttling engages at a higher
 *     zoom (i.e. more decimation on the default view), and
 *   - dropped MARKER_THROTTLE_CAP 60→32 so at most ~30 icons render below
 *     the threshold. Stable-hash dedup keeps the sampled set frame-stable.
 */
// Phase 17-poka2: user wants ALL buses visible on the overview (32 was
// too few — "32 of 55 buses · zoom in for all" felt broken). With the
// reduced halo opacity (fill 0.14, border 0.55) the map stays readable
// even at full-fleet density on the city-wide view.
const MARKER_THROTTLE_ZOOM = 14;
const MARKER_THROTTLE_CAP = 200;
/** Stable string hash so decimation is deterministic across frames. */
function _stableHash(id) {
    let h = 0;
    for (let i = 0; i < id.length; i++)
        h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h;
}
const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
export function BusMap({ envelope, routeId = "AS-4", demandActive = false, heatmapCells = [], heatmapWindowMinutes = 60, phantomLayerSlot = null, onBusClick, carpoolDrivers, carpoolAnchor, activeCarpoolRides, }) {
    // Feed-shape detection (3-way):
    //   isLive             → Delhi OTD (simulated=false)
    //   isMumbaiCorridor   → schedule-driven Mumbai/MMR feed (Phase 13a)
    //   else               → AS-4 legacy mock
    const isLive = !envelope.simulated;
    const isMumbaiCorridor = useMemo(() => !isLive && isMumbaiCorridorFeed(envelope.entities), [isLive, envelope.entities]);
    const center = useMemo(() => {
        if (isLive)
            return DELHI_CENTER.slice(0, 2);
        // Any non-live feed (AS-4 legacy mock OR Mumbai corridor mock) opens on
        // the corridor centroid so the buses are always in frame on load.
        return MUMBAI_CORRIDOR_CENTER.slice(0, 2);
    }, [isLive]);
    // D5: at low zoom Delhi (and Mumbai corridor) has many icons on top of each
    // other. We decimate to a stable 60 sample below MARKER_THROTTLE_ZOOM.
    const defaultZoom = isLive ? 11 : isMumbaiCorridor ? 11 : 12;
    const [zoom, setZoom] = useState(defaultZoom);
    const routeVehicles = useMemo(() => {
        // Mumbai corridor: show all Mumbai buses (30+ routes across the network),
        // decimating at low zoom exactly like Delhi.
        if (isMumbaiCorridor) {
            const all = envelope.entities;
            if (zoom >= MARKER_THROTTLE_ZOOM || all.length <= MARKER_THROTTLE_CAP) {
                return all;
            }
            const scored = all.map((e) => [_stableHash(e.vehicle.id), e]);
            scored.sort((a, b) => a[0] - b[0]);
            return scored.slice(0, MARKER_THROTTLE_CAP).map(([, e]) => e);
        }
        if (!isLive) {
            // AS-4 legacy mock: filter to the single corridor route id.
            return envelope.entities.filter((e) => e.trip.route_id === routeId);
        }
        // Delhi live: same decimation logic.
        const all = envelope.entities;
        if (zoom >= MARKER_THROTTLE_ZOOM || all.length <= MARKER_THROTTLE_CAP) {
            return all;
        }
        const scored = all.map((e) => [_stableHash(e.vehicle.id), e]);
        scored.sort((a, b) => a[0] - b[0]);
        return scored.slice(0, MARKER_THROTTLE_CAP).map(([, e]) => e);
    }, [envelope, routeId, isLive, isMumbaiCorridor, zoom]);
    const totalLive = isLive || isMumbaiCorridor ? envelope.entities.length : routeVehicles.length;
    const shownLive = routeVehicles.length;
    const decimated = (isLive || isMumbaiCorridor) && shownLive < totalLive;
    const cv = useHeadwayCv(isLive ? 6_000 : 60_000);
    // Phase 13e — click-a-bus route highlight.
    // `highlightedVehicle` stores the last-clicked bus; the RouteHighlightLayer
    // draws its full stop sequence on the map. The clay-ember halo pins the
    // "your bus" position; a cool-ash halo appears on the bunching partner.
    const mumbaiRoutes = useMumbaiRoutes();
    const [highlightedVehicle, setHighlightedVehicle] = useState(null);
    const highlightedRoute = highlightedVehicle
        ? mumbaiRoutes.byId[highlightedVehicle.trip.route_id] || null
        : null;
    // Bunching partner: nearest same-route bus within 400m of the highlighted one.
    const bunchingPartner = useMemo(() => {
        if (!highlightedVehicle)
            return null;
        const target = highlightedVehicle;
        let best = null;
        let bestDist = Infinity;
        for (const v of envelope.entities) {
            if (v.vehicle.id === target.vehicle.id)
                continue;
            if (v.trip.route_id !== target.trip.route_id)
                continue;
            const dLat = (v.position.lat - target.position.lat) * 111000;
            const dLng = (v.position.lng - target.position.lng) * 111000;
            const dist = Math.sqrt(dLat * dLat + dLng * dLng);
            if (dist < bestDist) {
                bestDist = dist;
                best = v;
            }
        }
        return best && bestDist < 400 ? { vehicle: best, distance_m: Math.round(bestDist) } : null;
    }, [highlightedVehicle, envelope.entities]);
    // Wrap the parent's onBusClick so we ALSO track selection locally.
    const handleBusClick = (v) => {
        setHighlightedVehicle(v);
        onBusClick?.(v);
    };
    return (_jsxs("div", { style: {
            position: "relative",
            width: "100%",
            height: "100%",
            minHeight: 320,
            backgroundColor: "var(--color-pebble)",
        }, "data-testid": "bus-map", "data-demand-active": demandActive ? "true" : "false", children: [_jsxs(MapContainer, { center: center, zoom: defaultZoom, minZoom: isLive || isMumbaiCorridor ? 9 : 11, maxZoom: 17, zoomControl: false, scrollWheelZoom: true, style: { width: "100%", height: "100%" }, children: [_jsx(TileLayer, { url: OSM_TILE_URL, attribution: OSM_ATTRIBUTION, maxNativeZoom: 19 }), _jsx(ZoomControl, { position: "bottomright" }), _jsx(ZoomWatcher, { onZoom: setZoom }), !isLive && !isMumbaiCorridor && (_jsxs(_Fragment, { children: [_jsx(CorridorPolyline, { stops: AS_4_STOPS }), _jsx(StopMarkers, { stops: AS_4_STOPS })] })), demandActive && _jsx(DemandHeatmap, { cells: heatmapCells }), highlightedRoute && highlightedVehicle && (_jsx(RouteHighlightLayer, { route: highlightedRoute, focusPosition: {
                            lat: highlightedVehicle.position.lat,
                            lng: highlightedVehicle.position.lng,
                        }, bunchingWithPosition: bunchingPartner
                            ? {
                                lat: bunchingPartner.vehicle.position.lat,
                                lng: bunchingPartner.vehicle.position.lng,
                            }
                            : undefined })), _jsx(VehicleLayer, { vehicles: routeVehicles, demandActive: demandActive, onBusClick: handleBusClick, focusedVehicleId: highlightedVehicle?.vehicle.id ?? null }), phantomLayerSlot, carpoolDrivers && carpoolDrivers.length > 0 ? (_jsx(CarpoolLayer, { drivers: carpoolDrivers, rider: carpoolAnchor })) : null, activeCarpoolRides && activeCarpoolRides.length > 0 ? (_jsx(ActiveCarpoolRidesLayer, { rides: activeCarpoolRides })) : null] }, isLive ? "delhi-live" : isMumbaiCorridor ? "mumbai-corridor" : "mumbai-as4"), envelope.simulated && (_jsx(SimulatedPill, { testId: "map-simulated-pill", style: { position: "absolute", top: 16, right: 16, zIndex: 500 } })), highlightedRoute && highlightedVehicle && (_jsx(BusDetailSheet, { route: highlightedRoute, vehicle: highlightedVehicle, bunchingPartner: bunchingPartner, onClose: () => setHighlightedVehicle(null) })), demandActive && (_jsx("div", { style: {
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    zIndex: 500,
                }, "data-testid": "heatmap-legend-anchor", children: _jsx(HeatmapLegend, { caption: `Last ${heatmapWindowMinutes} min` }) })), _jsxs("div", { className: "bb-corridor-caption", style: {
                    position: "absolute",
                    top: 12,
                    left: 12,
                    zIndex: 500,
                    padding: "8px 12px",
                    backgroundColor: "rgba(255,255,255,0.94)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid var(--color-pebble)",
                    borderRadius: 4,
                    fontFamily: "var(--font-display)",
                    maxWidth: 260,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }, "data-testid": "corridor-caption", children: [_jsx("div", { style: {
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "var(--color-cool-ash)",
                            marginBottom: 4,
                        }, children: isLive
                            ? "Delhi OTD · Live"
                            : isMumbaiCorridor
                                ? "Mumbai · MMR Corridor"
                                : "Corridor" }), _jsx("div", { style: {
                            fontSize: 16,
                            fontWeight: 700,
                            color: "var(--color-deep-ink)",
                            lineHeight: 1.15,
                        }, children: isLive
                            ? "DTC/DIMTS GTFS-RT · Delhi-wide"
                            : isMumbaiCorridor
                                ? "Panvel · Belapur · Vashi · Thane · Kalyan"
                                : "Route AS-4 · Andheri Stn → SEEPZ" }), _jsx("div", { style: {
                            fontSize: 13,
                            color: "var(--color-cool-ash)",
                            marginTop: 4,
                        }, children: isLive
                            ? decimated
                                ? `${shownLive} of ${totalLive} live · zoom in for all`
                                : `${totalLive} live vehicles · ${new Set(routeVehicles.map((v) => v.trip.route_id)).size} routes`
                            : isMumbaiCorridor
                                ? decimated
                                    ? `${shownLive} of ${totalLive} buses · zoom in for all`
                                    : `${totalLive} buses · ${new Set(routeVehicles.map((v) => v.trip.route_id)).size} routes · KDMT + TMT + BEST`
                                : `${routeVehicles.length} vehicles · 7 stops · 4.16 km` }), cv && cv.routes_with_pairs > 0 ? (_jsxs("div", { style: {
                            marginTop: 8,
                            paddingTop: 8,
                            borderTop: "1px dashed var(--color-pebble)",
                            fontSize: 11,
                            color: "var(--color-cool-ash)",
                            lineHeight: 1.4,
                        }, "data-testid": "headway-cv-chip", title: cv.note, children: [_jsxs("div", { children: [_jsxs("strong", { style: {
                                            color: cv.mean_cv > cv.baseline_cv
                                                ? "var(--color-clay-ember, #bc7155)"
                                                : "var(--color-deep-ink)",
                                            fontSize: 13,
                                        }, children: ["CV ", cv.mean_cv.toFixed(2)] }), " ", "vs UCLA ", cv.baseline_cv.toFixed(2), " baseline"] }), _jsxs("div", { style: { fontSize: 10 }, children: [cv.routes_with_pairs, "/", cv.routes_scanned, " routes with 2+ live"] })] })) : null] }), _jsx(PlaybackControls, {}), _jsx(OnboardingHint, {})] }));
}
